import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import CloseIcon from '@mui/icons-material/Close'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@emotion/react'
import { Trans, useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'

const PAGE_SIZE = 80

export default function GalleryWidget ({ orgId, eventId, cfRoot, apiBaseUrl, initialBib = '', resultsHref = '' }) {
  const { t } = useTranslation('race')
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const g = (key, opts) => t(`mediaGallery.${key}`, opts)
  const CF = (cfRoot || '').replace(/\/+$/, '')
  const [bib, setBib] = useState(initialBib)
  const [searchedBib, setSearchedBib] = useState('')
  const [photos, setPhotos] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [hasSearched, setHasSearched] = useState(false)
  const [view, setView] = useState('bib')
  const [untagged, setUntagged] = useState([])
  const [untaggedPage, setUntaggedPage] = useState(1)
  const [untaggedTotal, setUntaggedTotal] = useState(0)
  const [untaggedHasMore, setUntaggedHasMore] = useState(false)
  const [untaggedLoading, setUntaggedLoading] = useState(false)
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(null)
  const [activeUrl, setActiveUrl] = useState(null)
  const [viewerList, setViewerList] = useState([])
  const [viewerIndex, setViewerIndex] = useState(0)

  const normalizePhoto = (photo) => {
    if (typeof photo === 'string') {
      return {
        keyBase: photo,
        thumb: `thumb/${orgId}/${eventId}/${photo}`,
        download: `download/${orgId}/${eventId}/${photo}`
      }
    }
    const keyBase = photo.keyBase || (photo.thumb || photo.download || '').split('/').pop() || ''
    return {
      keyBase,
      thumb: photo.thumb || (keyBase ? `thumb/${orgId}/${eventId}/${keyBase}` : ''),
      download: photo.download || (keyBase ? `download/${orgId}/${eventId}/${keyBase}` : '')
    }
  }

  const buildThumb = (p) => {
    if (!p.thumb) return ''
    if (/^https?:\/\//i.test(p.thumb)) return p.thumb
    return `${CF}/${p.thumb.replace(/^\/+/, '')}`
  }

  const onSearch = async (bibValue = bib) => {
    const value = String(bibValue || '').trim()
    if (!value) return
    setLoading(true)
    setError(null)
    setView('bib')
    try {
      const r = await fetch(`${apiBaseUrl}/api/photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId, orgId, bib: value })
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(data.error || 'search failed')
      setPhotos((data.photos || []).map(normalizePhoto))
      setSearchedBib(value)
      setHasSearched(true)
      setUntagged([])
      setUntaggedPage(1)
      setUntaggedTotal(0)
      setUntaggedHasMore(false)
    } catch (e) {
      setError(e.name === 'TypeError' ? g('network_error') : (e.message || 'error'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (initialBib && eventId && orgId) onSearch(initialBib)
  }, [])

  const loadUntagged = async (page = 1, append = false) => {
    setUntaggedLoading(true)
    setError(null)
    try {
      const r = await fetch(`${apiBaseUrl}/api/photos`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ eventId, orgId, status: 'unidentified', page, pageSize: PAGE_SIZE })
      })
      const data = await r.json().catch(() => ({}))
      if (!r.ok) throw new Error(data.error || 'search failed')
      const next = (data.photos || []).map(normalizePhoto)
      setUntagged(prev => append ? [...prev, ...next] : next)
      setUntaggedPage(data.page || page)
      setUntaggedTotal(data.total || 0)
      setUntaggedHasMore(!!data.hasMore)
    } catch (e) {
      setError(e.name === 'TypeError' ? g('network_error') : (e.message || 'error'))
    } finally {
      setUntaggedLoading(false)
    }
  }

  const showUntagged = async () => {
    setView('unidentified')
    setUntagged([])
    setUntaggedPage(1)
    setUntaggedTotal(0)
    setUntaggedHasMore(false)
    await loadUntagged(1, false)
  }

  const backToSearch = () => {
    setView('bib')
    setError(null)
  }

  const signDownload = async (p) => {
    if (!p.download) throw new Error(g('download_unavailable'))
    const r = await fetch(`${apiBaseUrl}/api/sign-download?key=${encodeURIComponent(p.download)}`)
    const data = await r.json().catch(() => ({}))
    if (!r.ok || !data.url) throw new Error(data.error || g('download_failed'))
    return data.url
  }

  const openViewer = async (p, index = 0, list = []) => {
    setViewerList(list)
    setViewerIndex(index)
    setActive(p)
    setOpen(true)
    setActiveUrl(null)
    try {
      setActiveUrl(await signDownload(p))
    } catch (e) {
      setOpen(false)
      setError(e.name === 'TypeError' ? g('download_failed') : (e.message || g('download_failed')))
    }
  }

  const showViewerAt = async (index) => {
    if (!viewerList.length || index < 0 || index >= viewerList.length) return
    const p = viewerList[index]
    setViewerIndex(index)
    setActive(p)
    setActiveUrl(null)
    try {
      setActiveUrl(await signDownload(p))
    } catch (e) {
      setError(e.name === 'TypeError' ? g('download_failed') : (e.message || g('download_failed')))
    }
  }

  const downloadOne = async (p) => {
    try {
      const url = await signDownload(p)
      const a = document.createElement('a')
      a.href = url
      a.download = p.keyBase.split('/').pop() || 'photo.jpg'
      document.body.appendChild(a)
      a.click()
      a.remove()
    } catch (e) {
      setError(e.name === 'TypeError' ? g('download_failed') : (e.message || g('download_failed')))
    }
  }

  const downloadAll = async () => {
    setError(null)
    try {
      const r = await fetch(`${apiBaseUrl}/api/download-zip?eventId=${encodeURIComponent(eventId)}&bib=${encodeURIComponent(searchedBib)}`)
      if (!r.ok) throw new Error(g('download_all_unavailable'))
      const blob = await r.blob()
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${eventId}-bib-${searchedBib}.zip`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(a.href)
    } catch {
      setError(g('download_all_unavailable'))
    }
  }

  const renderPhotoCard = (p, index, list) => (
    <Grid key={p.keyBase} size={{ xs: 6, sm: 4, md: 3, lg: 2 }}>
      <Box sx={{ position: 'relative', overflow: 'hidden', borderRadius: 1 }}>
        <Box component='img' src={buildThumb(p)} alt={p.keyBase} sx={{ width: '100%', display: 'block', cursor: 'pointer' }} onClick={() => openViewer(p, index, list)} loading='lazy' />
        <Button
          size='small'
          onClick={() => downloadOne(p)}
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            right: 0,
            borderRadius: '0 0 4px 4px',
            color: '#fff',
            fontWeight: 700,
            py: 0.25,
            lineHeight: 1.2,
            textTransform: 'none',
            backgroundColor: 'rgba(0, 0, 0, 0.25)',
            backdropFilter: 'blur(4px)',
            WebkitBackdropFilter: 'blur(4px)',
            borderTop: '1px solid rgba(255, 255, 255, 0.15)'
          }}
        >
          {g('download')}
        </Button>
      </Box>
    </Grid>
  )

  return (
    <Box>
      <Typography variant='h6'>{g('find_your_photos')}</Typography>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', my: 2 }}>
        <TextField
          label={g('bib_number')}
          value={bib}
          onChange={e => setBib(e.target.value.replace(/\D/g, ''))}
          sx={{ '& .MuiOutlinedInput-root': { backgroundColor: 'rgba(0, 0, 0, 0.35)' } }}
        />
        <Button variant='contained' onClick={() => onSearch()} disabled={!bib || loading}>{loading ? g('searching') : g('search')}</Button>
      </Box>

      {resultsHref && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          <Trans
            ns='race'
            i18nKey='mediaGallery.bib_hint'
            components={{ results: <RouterLink to={resultsHref} style={{ color: 'inherit', textDecoration: 'underline' }} /> }}
          />
        </Typography>
      )}

      {error && <Typography color='error' sx={{ mb: 1 }}>{error}</Typography>}

      {view === 'bib' ? (
        <>
          {hasSearched && photos.length === 0 && !loading && (
            <Typography sx={{ mb: 1 }}>{g('no_photos_for_bib', { bib: searchedBib })}</Typography>
          )}
          {photos.length > 0 && (
            <Button variant='outlined' onClick={() => downloadAll()} sx={{ mb: 1 }}>{g('download_all')}</Button>
          )}
          {hasSearched && (
            <Box sx={{ mb: 2 }}>
              <Button variant='text' onClick={showUntagged}>{g('view_untagged')}</Button>
            </Box>
          )}
          <Grid container spacing={2}>
            {photos.map((p, i) => renderPhotoCard(p, i, photos))}
          </Grid>
        </>
      ) : (
        <>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
            <Typography variant='subtitle1'>{g('untagged_photos')}</Typography>
            <Typography variant='body2' color='text.secondary'>({untaggedTotal})</Typography>
            <Button variant='text' onClick={backToSearch}>{g('back_to_search')}</Button>
          </Box>
          {untaggedLoading && untagged.length === 0 && <Typography sx={{ mb: 1 }}>{g('searching')}</Typography>}
          {!untaggedLoading && untagged.length === 0 && <Typography sx={{ mb: 1 }}>{g('no_untagged')}</Typography>}
          <Grid container spacing={2}>
            {untagged.map((p, i) => renderPhotoCard(p, i, untagged))}
          </Grid>
          {untaggedHasMore && (
            <Button variant='outlined' onClick={() => loadUntagged(untaggedPage + 1, true)} disabled={untaggedLoading} sx={{ mt: 2 }}>
              {untaggedLoading ? g('searching') : g('load_more')}
            </Button>
          )}
        </>
      )}

      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth='lg'
        fullWidth
        fullScreen={isMobile}
        slotProps={{ paper: { sx: { height: isMobile ? '100%' : '98vh', maxHeight: isMobile ? '100%' : '98vh' } } }}
      >
        <DialogTitle>{g('photo_preview')}<IconButton onClick={() => setOpen(false)} sx={{ position: 'absolute', right: 8, top: 8 }}><CloseIcon /></IconButton></DialogTitle>
        <DialogContent dividers sx={{ position: 'relative', p: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 0, overflow: 'hidden' }}>
          {active && activeUrl && (<Box component='img' src={activeUrl} alt={active.keyBase} sx={{ width: '100%', height: '100%', maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', display: 'block' }} />)}
          {view === 'bib' && viewerList.length > 1 && (
            <>
              <IconButton
                aria-label={g('previous')}
                onClick={() => showViewerAt(viewerIndex - 1)}
                disabled={viewerIndex === 0}
                sx={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.45)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' }, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)', backgroundColor: 'rgba(0, 0, 0, 0.2)' } }}
              >
                <ChevronLeftIcon />
              </IconButton>
              <IconButton
                aria-label={g('next')}
                onClick={() => showViewerAt(viewerIndex + 1)}
                disabled={viewerIndex === viewerList.length - 1}
                sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', color: '#fff', backgroundColor: 'rgba(0, 0, 0, 0.45)', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.65)' }, '&.Mui-disabled': { color: 'rgba(255, 255, 255, 0.3)', backgroundColor: 'rgba(0, 0, 0, 0.2)' } }}
              >
                <ChevronRightIcon />
              </IconButton>
              <Box sx={{ position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)', display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 0.75, maxWidth: '90%', px: 1.5, py: 0.75, borderRadius: 999, backgroundColor: 'rgba(0, 0, 0, 0.45)' }}>
                {viewerList.map((item, i) => (
                  <Box
                    key={item.keyBase || i}
                    component='button'
                    type='button'
                    aria-label={`${i + 1} / ${viewerList.length}`}
                    aria-current={i === viewerIndex}
                    onClick={() => showViewerAt(i)}
                    sx={{ width: 8, height: 8, p: 0, border: 0, borderRadius: '50%', cursor: 'pointer', backgroundColor: i === viewerIndex ? '#fff' : 'rgba(255, 255, 255, 0.45)', transition: 'background-color 150ms' }}
                  />
                ))}
              </Box>
            </>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  )
}
